import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Pencil, Send, Undo2, X, Eye } from "lucide-react";
import { useContent } from "@/lib/content";

export const LiveEditBar = () => {
  const { liveActive, previewMode, setPreview, pendingCount, publish, discard, exitEditMode } = useContent();
  const navigate = useNavigate();
  if (!liveActive) return null;

  const exit = () => {
    if (pendingCount && !window.confirm(`You have ${pendingCount} unpublished change(s). Exit and discard them?`)) return;
    exitEditMode();
    navigate("/admin/live-edit");
  };

  const publishLabel = `Publish${pendingCount ? ` ${pendingCount} change${pendingCount === 1 ? "" : "s"}` : " changes"}`;

  if (previewMode) {
    return (
      <div data-testid="live-edit-bar" data-mode="preview" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 px-4 py-2.5 rounded-full bg-jade-deep/95 text-ivory shadow-2xl backdrop-blur-md border border-ivory/10">
        <span className="flex items-center gap-2 font-body text-xs tracking-widest uppercase text-champagne-gold pr-2 border-r border-ivory/15">
          <Eye className="w-3.5 h-3.5" /> Preview
        </span>
        <span data-testid="live-edit-pending-count" className="font-body text-xs text-ivory/80 whitespace-nowrap">
          {pendingCount ? `Showing ${pendingCount} unpublished change${pendingCount === 1 ? "" : "s"} as visitors will see them` : "No unpublished changes — this is the live site"}
        </span>
        <Button size="sm" variant="ghost" className="text-ivory/80 hover:text-ivory hover:bg-white/10 h-8" onClick={() => setPreview(false)} data-testid="live-edit-back-to-editing-btn">
          <Pencil className="w-3.5 h-3.5 mr-1" /> Back to editing
        </Button>
        <Button size="sm" className="bg-champagne-gold text-charcoal hover:bg-champagne h-8 rounded-full px-4" disabled={!pendingCount} onClick={publish} data-testid="live-edit-publish-btn">
          <Send className="w-3.5 h-3.5 mr-1" /> {publishLabel}
        </Button>
        <button type="button" onClick={exit} aria-label="Exit edit mode" className="p-1.5 rounded-full hover:bg-white/10 text-ivory/70 hover:text-ivory" data-testid="live-edit-exit-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="live-edit-bar" data-mode="edit" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 px-4 py-2.5 rounded-full bg-charcoal/95 text-ivory shadow-2xl backdrop-blur-md border border-ivory/10">
      <span className="flex items-center gap-2 font-body text-xs tracking-widest uppercase text-champagne-gold pr-2 border-r border-ivory/15">
        <Pencil className="w-3.5 h-3.5" /> Live Edit
      </span>
      <span data-testid="live-edit-pending-count" className="font-body text-xs text-ivory/70 whitespace-nowrap">
        {pendingCount ? `${pendingCount} unpublished change${pendingCount === 1 ? "" : "s"}` : "Click any text or photo to edit"}
      </span>
      <Button size="sm" variant="ghost" className="text-ivory/80 hover:text-ivory hover:bg-white/10 h-8" onClick={() => setPreview(true)} data-testid="live-edit-preview-btn">
        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
      </Button>
      <Button size="sm" variant="ghost" className="text-ivory/80 hover:text-ivory hover:bg-white/10 h-8" disabled={!pendingCount} onClick={discard} data-testid="live-edit-discard-btn">
        <Undo2 className="w-3.5 h-3.5 mr-1" /> Discard
      </Button>
      <Button size="sm" className="bg-champagne-gold text-charcoal hover:bg-champagne h-8 rounded-full px-4" disabled={!pendingCount} onClick={publish} data-testid="live-edit-publish-btn">
        <Send className="w-3.5 h-3.5 mr-1" /> {publishLabel}
      </Button>
      <button type="button" onClick={exit} aria-label="Exit edit mode" className="p-1.5 rounded-full hover:bg-white/10 text-ivory/70 hover:text-ivory" data-testid="live-edit-exit-btn">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
